import {
    ClientReportSubmitInput,
    isAddendumRequestReasonRequired,
    requireServiceType,
} from "../domain";
import {useOktaAuth} from "@okta/okta-react";
import {isEmpty} from "lodash";

const REPORT_SUBMISSION_API_URL = `${process.env.REACT_APP_API_URL}/api/client-report-submit`;

export function buildClientReportPayload(data: ClientReportSubmitInput, shouldForceCreateNewOperationCase: boolean = false, shouldUpdateActiveAuth: boolean = false): FormData {
    const formData = new FormData();

    if (data.authorizationGuid) {
        formData.append('authorizationGuid', data.authorizationGuid);
    }
    if (data.reportType) {
        formData.append('reportType', data.reportType);
    }
    if (data.firstTreatmentOfferedDate) {
        formData.append('firstTreatmentOfferedDate', data.firstTreatmentOfferedDate);
    }
    if (data.lastDayOfServiceBilled) {
        formData.append('lastDayOfServiceBilled', data.lastDayOfServiceBilled);
    }
    if (data.newAuthorizationStartDate) {
        formData.append('newAuthorizationStartDate', data.newAuthorizationStartDate);
    }
    if (data.finalDiagnosticContactDate && !!data.finalDiagnosticContactDate) {
        formData.append('finalDiagnosticContactDate', data.finalDiagnosticContactDate);
    }
    if (data.vinelandIncluded && !!data.vinelandIncluded) {
        formData.append('vinelandIncluded', data.vinelandIncluded);
    }

    if (data.anticipatedDischargeDate) {
        formData.append('anticipatedDischargeDate', data.anticipatedDischargeDate);
    }
    if (data.comments) {
        formData.append('description', data.comments);
    }
    if (data.clinicalRationale) {
        formData.append('clinicalRationale', data.clinicalRationale);
    }
    if (data.nameAndTitle) {
        formData.append('nameAndTitle', data.nameAndTitle);
    }
    if (data.licenseNumber) {
        formData.append('licenseNumber', data.licenseNumber);
    }
    if (requireServiceType(data.reportType)) {
        if (!isEmpty(data.serviceRecommended)) {
            formData.append('serviceRecommended', data.serviceRecommended.toString());
        }

        formData.append('serviceType', data.serviceType?.toString());

        if (data.caregiverMidLevelHoursMin != null) {
            formData.append('caregiverMidLevelHoursMin', data.caregiverMidLevelHoursMin.toString());
        }
        if (data.caregiverMidLevelHoursMax != null) {
            formData.append('caregiverMidLevelHoursMax', data.caregiverMidLevelHoursMax.toString());
        }
        if (data.caregiverHighLevelHoursMin != null) {
            formData.append('caregiverHighLevelHoursMin', data.caregiverHighLevelHoursMin.toString());
        }
        if (data.caregiverHighLevelHoursMax != null) {
            formData.append('caregiverHighLevelHoursMax', data.caregiverHighLevelHoursMax.toString());
        }
        if (data.practitionerParaprofessionalHoursMin != null) {
            formData.append('practitionerParaprofessionalHoursMin', data.practitionerParaprofessionalHoursMin.toString());
        }
        if (data.practitionerParaprofessionalHoursMax != null) {
            formData.append('practitionerParaprofessionalHoursMax', data.practitionerParaprofessionalHoursMax.toString());
        }
        if (data.practitionerMidLevelHoursMin != null) {
            formData.append('practitionerMidLevelHoursMin', data.practitionerMidLevelHoursMin.toString());
        }
        if (data.practitionerMidLevelHoursMax != null) {
            formData.append('practitionerMidLevelHoursMax', data.practitionerMidLevelHoursMax.toString());
        }
        if (data.practitionerHighLevelHoursMin != null) {
            formData.append('practitionerHighLevelHoursMin', data.practitionerHighLevelHoursMin.toString());
        }
        if (data.practitionerHighLevelHoursMax != null) {
            formData.append('practitionerHighLevelHoursMax', data.practitionerHighLevelHoursMax.toString());
        }
        if (data.practitionerGroupCareHoursMin != null) {
            formData.append('practitionerGroupCareHoursMin', data.practitionerGroupCareHoursMin.toString());
        }
        if (data.practitionerGroupCareHoursMax != null) {
            formData.append('practitionerGroupCareHoursMax', data.practitionerGroupCareHoursMax.toString());
        }
        if (data.offeredParaprofessionalHours != null) {
            formData.append('offeredParaprofessionalHours', data.offeredParaprofessionalHours.toString());
        }
        if (data.offeredMidLevelHours != null) {
            formData.append('offeredMidLevelHours', data.offeredMidLevelHours.toString());
        }
        if (data.offeredHighLevelHours != null) {
            formData.append('offeredHighLevelHours', data.offeredHighLevelHours.toString());
        }
        if (data.offeredGroupCareHours != null) {
            formData.append('offeredGroupCareHours', data.offeredGroupCareHours.toString());
        }
    }
    if (data.treatmentOffered != null) {
        formData.append('treatmentOffered', data.treatmentOffered.toString());
    }

    if (isAddendumRequestReasonRequired(data.reportType) && data.addendumRequestReason) {
        formData.append('addendumRequestReason', data.addendumRequestReason.toString());
    }

    if (data.recommendationDifferenceRationale) {
        formData.append('recommendationDifferenceRationale', data.recommendationDifferenceRationale);
    }

    if (shouldForceCreateNewOperationCase) {
        formData.append('forceCreateNewOperationCase', 'YES');
    } else {
        formData.append('forceCreateNewOperationCase', 'NO');
    }

    if (shouldUpdateActiveAuth) {
        formData.append('shouldUpdateActiveAuth', 'YES');
    } else {
        formData.append('shouldUpdateActiveAuth', 'NO');
    }

    return formData;
}

export function useOktaAccessToken() {
    const oktaAuthState = useOktaAuth();
    if (oktaAuthState && oktaAuthState.oktaAuth) {
        return oktaAuthState.oktaAuth.authStateManager.getAuthState()?.accessToken?.accessToken;
    }
    return '';
}

export function submitReport(clientReportPayload: FormData, token?: string) {
    return fetch(REPORT_SUBMISSION_API_URL, {
        method: 'POST',
        headers: {
            Authorization: token ? `Bearer ${token}` : "",
        },
        body: clientReportPayload
    });
}
